import { NgIf } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { CardComponent } from '@components/card/card.component';
import { ChooseAPharmacyComponent } from '@components/consultation-request/choose-a-pharmacy/choose-a-pharmacy.component';
import { ConsultationRequestAddressComponent } from '@components/consultation-request/consultation-request-address/consultation-request-address.component';
import { ConsultationRequestFormComponent } from '@components/consultation-request/consultation-request-form/consultation-request-form.component';
import { ConsultationRequestPatientInfoComponent } from '@components/consultation-request/consultation-request-patient-info/consultation-request-patient-info.component';
import { LabFinderModalComponent } from '@components/consultation-request/lab-finder-modal/lab-finder-modal.component';
import { MedicalHistoryQuestionsComponent } from '@components/consultation-request/medical-history-questions/medical-history-questions.component';
import { StdPreventionUpgradeDosageUpsellComponent } from '@components/consultation-request/upsells/upgrade-dosage-upsell/std-prevention-upgrade-dosage-upsell/std-prevention-upgrade-dosage-upsell.component';
import { ConsultationRequestHeaderComponent } from '@components/consultation-request-header/consultation-request-header.component';
import { ConsultationRequestTreatmentPreferencesCardComponent } from '@components/consultation-request-treatment-preferences-card/consultation-request-treatment-preferences-card.component';
import { DynamicQuestionnaireFormComponent } from '@components/dynamic-forms/dynamic-questionnaire-form/dynamic-questionnaire-form.component';
import { ErrorNotificationComponent } from '@components/error-notification/error-notification.component';
import { ConsultationTreatmentTypes } from '@enums/consultation-treatment-types';
import { UpdatePharmacyError } from '@errors/update-pharmacy-error';
import { ConsultationRequestPharmacy } from '@models/consultation-request/consultation-request-pharmacy';
import { Observable, Subscription } from 'rxjs';
import stdPreventionDosagesJson from 'src/assets/questionnaires/std-prevention-dosages.json';

@Component({
  selector: 'app-dynamic-consultation-request-form',
  templateUrl: 'dynamic-consultation-request-form.component.html',
  styleUrls: ['dynamic-consultation-request-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    NgIf,
    ErrorNotificationComponent,
    ConsultationRequestHeaderComponent,
    ConsultationRequestPatientInfoComponent,
    ChooseAPharmacyComponent,
    ConsultationRequestTreatmentPreferencesCardComponent,
    CardComponent,
    DynamicQuestionnaireFormComponent,
    MedicalHistoryQuestionsComponent,
    LabFinderModalComponent,
    ConsultationRequestAddressComponent,
    StdPreventionUpgradeDosageUpsellComponent,
  ],
})
export class DynamicConsultationRequestFormComponent
  extends ConsultationRequestFormComponent
  implements AfterViewInit, OnDestroy
{
  @ViewChild('upsellQuestionsForm', { static: false }) private upsellQuestionsForm: DynamicQuestionnaireFormComponent;
  @ViewChild('chooseAPharmacyComponent', { static: false }) private chooseAPharmacyComponent: ChooseAPharmacyComponent;
  @ViewChild('stdPreventionUpgradeDosageUpsell', { static: false })
  private stdPreventionUpgradeDosageUpsell: StdPreventionUpgradeDosageUpsellComponent;
  public pharmacyState: string | null = null;

  protected changeDetectorRef = inject(ChangeDetectorRef);

  private subscriptions: Subscription[] = [];

  /**
   * Determines if the consultation request is std prevention type.
   */
  public get isStdPrevention(): boolean {
    return this.consultationRequestTreatmentType === ConsultationTreatmentTypes.StdPrevention;
  }

  /**
   * Gets the STD prevention test ID from the order.
   * Searches for DoxyPEP test in customer_tests (1, 15, or 30 treatments).
   */
  public get stdPreventionTestId(): number | undefined {
    return this.consultationRequestOrder?.customer_tests?.find((customerTest) =>
      stdPreventionDosagesJson.some((dosage) => dosage.id === customerTest.test?.id),
    )?.test?.id;
  }

  /**
   * Gets the preselected pharmacy for the consultation request.
   */
  public get preselectedPharmacy(): ConsultationRequestPharmacy {
    return this.consultationRequestOrder?.consultationRequest.pharmacy;
  }

  /**
   * Gets the upsell questions form.
   */
  private get upsellForm(): FormGroup {
    return this.upsellQuestionsForm?.form;
  }

  /**
   * Gets the STD prevention upgrade dosage form.
   */
  private get stdPreventionUpgradeDosageForm(): FormGroup | undefined {
    return this.stdPreventionUpgradeDosageUpsell?.form;
  }

  /**
   * Initializes the pharmacy state tracking logic.
   */
  public ngAfterViewInit(): void {
    this.initializePharmacyStateLogic();
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  /**
   * {@inheritDoc}
   * Adds touching upsell and STD prevention dosage upgrade forms.
   */
  protected override markAllFormControlsAsTouched(): void {
    super.markAllFormControlsAsTouched();
    this.upsellForm?.markAllAsTouched();
    this.stdPreventionUpgradeDosageForm?.markAllAsTouched();
  }

  /**
   * {@inheritDoc}
   * Adds checks for upsell and STD prevention dosage upgrade forms validity.
   */
  protected override areFormsValid(): boolean {
    return super.areFormsValid() && !this.upsellForm?.invalid && !this.stdPreventionUpgradeDosageForm?.invalid;
  }

  /**
   * {@inheritDoc}
   * Check if the forms are touched, including the upsell form.
   */
  protected override areFormsTouched(): boolean {
    return super.areFormsTouched() && (this.upsellForm?.touched ?? true);
  }

  /**
   * {@inheritDoc}
   * Handles pharmacy update check before calling base success workflow.
   */
  protected override handleSuccessWorkflow() {
    if (this.shouldUpdatePharmacy()) {
      this.handlePharmacyUpdate();

      return;
    }

    super.handleSuccessWorkflow();
  }

  /** @inheritdoc */
  protected override presentLabFinderModal(): void {
    if (this.chooseAPharmacyComponent) {
      this.chooseAPharmacyComponent.changingLab = false;
    }

    super.presentLabFinderModal();
  }

  /**
   * Checks if the pharmacy should be updated.
   *
   * @returns {boolean} true if the pharmacy should be updated, otherwise false
   */
  protected shouldUpdatePharmacy(): boolean {
    return this.consultationRequest?.pharmacy?.id !== this.pharmacyForm.value.id;
  }

  /**
   * Patches the consultation request pharmacy ID with the selected pharmacy ID to ensure the order is updated with the correct pharmacy.
   */
  private handlePharmacyUpdate(): void {
    this.updatePharmacy().subscribe({
      next: () => super.handleSuccessWorkflow(),
      error: () => {
        this.isProcessing = false;
        this.responseError = new UpdatePharmacyError().message;
      },
    });
  }

  /**
   * Updates the consultation request pharmacy.
   */
  private updatePharmacy(): Observable<any> {
    return this.consultationRequestService.update(
      this.consultationRequest.id,
      this.consultationRequestOrder.transaction_id,
      this.consultationRequestOrder.hash,
      {
        pharmacy_id: this.pharmacyForm.value.id,
      },
    );
  }

  /**
   * Initializes the pharmacy state tracking logic.
   */
  private initializePharmacyStateLogic(): void {
    if (!this.pharmacyForm) {
      return;
    }

    this.pharmacyState = this.pharmacyForm.value.state;
    const stateControl = this.pharmacyForm.get('state');
    if (stateControl) {
      this.subscriptions.push(
        stateControl.valueChanges.subscribe((state) => {
          this.pharmacyState = state;
          if (this.changeDetectorRef) {
            this.changeDetectorRef.detectChanges();
          }
        }),
      );
    }
  }
}
